import React, { useCallback, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    LinearProgress,
    Alert,
    Chip,
} from "@mui/material";
import { CloudUpload, Close } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";

interface ImageUploadZoneProps {
    onUpload: (files: File[]) => Promise<void>;
    maxFiles?: number;
    maxSize?: number;
    disabled?: boolean;
}

interface FileWithPreview extends File {
    preview?: string;
}

export const ImageUploadZone: React.FC<ImageUploadZoneProps> = ({
    onUpload,
    maxFiles = 10,
    maxSize = 10 * 1024 * 1024, // 10MB
    disabled = false,
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);

    const onDrop = useCallback(
        (acceptedFiles: File[], rejectedFiles: any[]) => {
            setError(null);

            if (rejectedFiles.length > 0) {
                const errors = rejectedFiles.map((file) => {
                    if (file.errors[0]?.code === "file-too-large") {
                        return `${file.file.name}: File too large (max ${maxSize / 1024 / 1024}MB)`;
                    }
                    if (file.errors[0]?.code === "file-invalid-type") {
                        return `${file.file.name}: Invalid file type`;
                    }
                    return `${file.file.name}: ${file.errors[0]?.message}`;
                });
                setError(errors.join(", "));
                return;
            }

            if (acceptedFiles.length + selectedFiles.length > maxFiles) {
                setError(`Maximum ${maxFiles} files allowed`);
                return;
            }

            const filesWithPreview = acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                }),
            );

            setSelectedFiles((prev) => [...prev, ...filesWithPreview]);
        },
        [maxFiles, maxSize, selectedFiles.length],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
        },
        maxSize,
        maxFiles,
        disabled: disabled || uploading,
    });

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => {
            const newFiles = [...prev];
            if (newFiles[index].preview) {
                URL.revokeObjectURL(newFiles[index].preview!);
            }
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);
        setError(null);

        try {
            await onUpload(selectedFiles);

            selectedFiles.forEach((file) => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
            setSelectedFiles([]);
        } catch (err: any) {
            setError(err.message || "Failed to upload images");
        } finally {
            setUploading(false);
        }
    };

    React.useEffect(() => {
        return () => {
            selectedFiles.forEach((file) => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, [selectedFiles]);

    return (
        <Box>
            <Paper
                {...getRootProps()}
                sx={{
                    p: 3,
                    border: "2px dashed",
                    borderColor: isDragActive ? "primary.main" : "grey.300",
                    bgcolor: isDragActive ? "action.hover" : "background.paper",
                    cursor: disabled || uploading ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                        borderColor:
                            disabled || uploading ? "grey.300" : "primary.main",
                        bgcolor:
                            disabled || uploading
                                ? "background.paper"
                                : "action.hover",
                    },
                }}
            >
                <input {...getInputProps()} />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <CloudUpload sx={{ fontSize: 48, color: "primary.main" }} />
                    <Typography variant="h6">
                        {isDragActive
                            ? "Drop images here"
                            : "Drag & drop images or click to browse"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Supports: JPEG, PNG, GIF, WebP (max{" "}
                        {maxSize / 1024 / 1024}MB per file)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Maximum {maxFiles} files
                    </Typography>
                </Box>
            </Paper>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Selected Files ({selectedFiles.length})
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {selectedFiles.map((file, index) => (
                            <Chip
                                key={index}
                                label={file.name}
                                onDelete={() => removeFile(index)}
                                deleteIcon={<Close />}
                                sx={{ maxWidth: 200 }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            style={{
                                padding: "8px 16px",
                                backgroundColor: uploading ? "#ccc" : "#1976d2",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: uploading ? "not-allowed" : "pointer",
                            }}
                        >
                            {uploading
                                ? "Uploading..."
                                : `Upload ${selectedFiles.length} file(s)`}
                        </button>
                        <button
                            onClick={() => setSelectedFiles([])}
                            disabled={uploading}
                            style={{
                                padding: "8px 16px",
                                backgroundColor: "transparent",
                                color: "#666",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                cursor: uploading ? "not-allowed" : "pointer",
                            }}
                        >
                            Clear All
                        </button>
                    </Box>
                </Box>
            )}

            {uploading && (
                <Box sx={{ mt: 2 }}>
                    <LinearProgress variant="indeterminate" />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                    >
                        Uploading images...
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
